/**
 * The live arrival (`spec-onboarding-persistent-scene-launch-into-live`, R3, R4): the camera Launch
 * shows, handed to the live viewer's map so it opens there, flat, and flies on to the twin's opening
 * view.
 *
 * Two things, held outside React and any store, as `planPoseHandback.ts` holds its pose:
 *
 * - **The stage camera reader.** The onboarding stage's washed plan (Launch's map) registers a
 *   function that reads its camera while it is on screen; the landing reads it once.
 * - **The request.** One-shot: the launched twin's BID and that camera, stamped when made. The live
 *   viewer's `MapCanvas` takes it once at mount, only for the twin the viewer holds and only within
 *   `LIVE_ARRIVAL_TTL_MS`; anything else is discarded and the viewer opens as it always has.
 *
 * And the arrival's progress (R4): how far the camera has travelled from the plan view to the
 * opening view, which the machines' height follows.
 *
 * Nothing here is stored, logged or sent anywhere (spec, Access and Data Exposure).
 */

/** A plan-view camera, as the stage map shows it. Pitch is always 0 (R3). */
export interface StageCamera {
  center: [number, number];
  zoom: number;
  bearing: number;
}

export interface LiveArrival extends StageCamera {
  twinBid: string;
  pitch: 0;
  /** `Date.now()` when the landing made the request. */
  at: number;
}

/** How long a request waits for the live map to take it (R3.2). */
export const LIVE_ARRIVAL_TTL_MS = 10_000;

type StageCameraReader = () => StageCamera | null;

let reader: StageCameraReader | null = null;
let request: LiveArrival | null = null;

const finiteCamera = (camera: StageCamera | null): camera is StageCamera =>
  !!camera &&
  Array.isArray(camera.center) &&
  Number.isFinite(camera.center[0]) &&
  Number.isFinite(camera.center[1]) &&
  Number.isFinite(camera.zoom) &&
  Number.isFinite(camera.bearing);

/**
 * The stage map on screen offers its camera; the returned function withdraws it, only if it is
 * still the one registered, so a later map's registration is never withdrawn by an earlier one.
 */
export function registerStageCamera(read: StageCameraReader): () => void {
  reader = read;
  return () => {
    if (reader === read) reader = null;
  };
}

/** The stage's camera now, or null with no stage map or any non-finite value (spec, Inputs). */
export function readStageCamera(): StageCamera | null {
  let camera: StageCamera | null = null;
  try {
    camera = reader?.() ?? null;
  } catch {
    // The map is being removed.
    camera = null;
  }
  if (!finiteCamera(camera)) return null;
  return {
    center: [camera.center[0], camera.center[1]],
    zoom: camera.zoom,
    bearing: camera.bearing,
  };
}

/** R3: the landing's request. Replaces any request not yet taken. */
export function requestLiveArrival(twinBid: string, camera: StageCamera): void {
  request = {
    twinBid,
    center: [camera.center[0], camera.center[1]],
    zoom: camera.zoom,
    bearing: camera.bearing,
    pitch: 0,
    at: Date.now(),
  };
}

/**
 * R3, R3.3: read the stage camera and request the arrival. False, with no request made, when no
 * stage camera can be read: the viewer then opens at the opening view, as it always has.
 */
export function requestArrivalFromStage(twinBid: string): boolean {
  const camera = readStageCamera();
  if (!camera || twinBid.trim() === '') return false;
  requestLiveArrival(twinBid, camera);
  return true;
}

/**
 * R3.1, R3.2: the live map is mounting for `twinBid`. Returns the request and clears it; one for
 * another twin, or older than `LIVE_ARRIVAL_TTL_MS`, is cleared and ignored.
 */
export function takeLiveArrival(twinBid: string | null | undefined): LiveArrival | null {
  const waiting = request;
  request = null;
  if (!waiting || !twinBid || waiting.twinBid !== twinBid) return null;
  if (Date.now() - waiting.at > LIVE_ARRIVAL_TTL_MS) return null;
  return waiting;
}

/** For tests: forget everything. */
export function resetLiveArrivalForTests(): void {
  reader = null;
  request = null;
}

/** A map camera, in react-map-gl's view-state shape. */
export interface ViewCamera {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

const mercator = (longitude: number, latitude: number): [number, number] => {
  const lat = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  const sin = Math.sin((lat * Math.PI) / 180);
  return [(longitude + 180) / 360, 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)];
};

/** The shortest turn from `from` to `to`, in degrees, in (-180, 180]. */
const turn = (from: number, to: number) => ((((to - from) % 360) + 540) % 360) - 180;

/**
 * How far apart two cameras are, in one unitless measure: the centre's offset in screen pixels at
 * `dest`'s zoom (per 1,000 px), the zoom per 4 levels, the pitch per 60° and the bearing per 180°.
 */
const separation = (now: ViewCamera, dest: ViewCamera): number => {
  const [x0, y0] = mercator(now.longitude, now.latitude);
  const [x1, y1] = mercator(dest.longitude, dest.latitude);
  const worldPx = 512 * Math.pow(2, dest.zoom);
  const centre = (Math.hypot(x1 - x0, y1 - y0) * worldPx) / 1000;
  return Math.hypot(
    centre,
    (dest.zoom - now.zoom) / 4,
    (dest.pitch - now.pitch) / 60,
    turn(now.bearing, dest.bearing) / 180,
  );
};

/**
 * R4: the arrival flight's progress, 0 at `start` and 1 at `dest`, as the fraction of the distance
 * the camera has travelled. Monotonic: a flight that briefly moves away (zooming out mid-flight)
 * never takes the height back down. A start already at the destination is 1.
 */
export function createArrivalProgress(
  start: ViewCamera,
  dest: ViewCamera,
): (now: ViewCamera) => number {
  const total = separation(start, dest);
  let best = 0;
  return now => {
    if (!(total > 1e-9)) return 1;
    const p = 1 - separation(now, dest) / total;
    if (Number.isFinite(p)) best = Math.max(best, Math.min(1, p));
    return best;
  };
}

/** R4: the height scale for a progress, eased (smoothstep) from `rest` to 1. */
export function arrivalHeightScale(progress: number, rest: number): number {
  const p = Math.max(0, Math.min(1, progress));
  const eased = p * p * (3 - 2 * p);
  return rest + (1 - rest) * eased;
}

/**
 * Development builds only: the launch-into-live rig reads the stage camera the landing would carry
 * (spec criterion 6). Never in a production build.
 */
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__stageCameraProbe = readStageCamera;
}
